/**
 * InferField — an inline, playable mini-demo: type a field name + a sample
 * value and watch Inferrex classify it live, with the value "flowing" through
 * the inference pipeline stages. Reuses the same client-side classifier as the
 * full FlowDemo (flow-demo-data). No network; deterministic.
 *
 * It's the lightweight, embeddable cousin of the /demo FlowDemo — drop it on the
 * home/functionality pages so the first scroll has something to interact with.
 */
import { useState, type CSSProperties } from 'react';
import { Card } from '../card';
import { Badge } from '../badge';
import { Input } from '../input';
import { Label } from '../label';
import { cn, confidenceColor, confidenceLabel } from '../../utils';
import { inferField } from './flow-demo-data';

export interface InferFieldProps {
  /** Override the starting field name/value. */
  readonly initialName?: string;
  readonly initialValue?: string;
}

const STAGES = ['Read', 'Classify', 'Relate', 'Interpret', 'Match'] as const;

const PRESETS: ReadonlyArray<{ label: string; name: string; value: string }> = [
  { label: 'Email', name: 'customer_email', value: 'ada@acme.io' },
  { label: 'Amount', name: 'mrr_amount', value: '4999' },
  { label: 'Date', name: 'signup_date', value: '2026-03-01' },
  { label: 'Phone', name: 'mobile', value: '+44 7700 900123' },
  { label: 'Cryptic', name: 'x_ref_9', value: 'usr_8KZ2a' },
];

/** Coerce a raw string into the value type the classifier expects. */
function coerce(v: string): unknown {
  const t = v.trim();
  if (t === 'true' || t === 'false') return t === 'true';
  if (t !== '' && /^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  return v;
}

export function InferField({ initialName = 'customer_email', initialValue = 'ada@acme.io' }: InferFieldProps) {
  const [name, setName] = useState(initialName);
  const [value, setValue] = useState(initialValue);

  const result = inferField(name, coerce(value));
  const pct = Math.round(result.confidence * 100);
  const barClass = { high: 'bg-success', medium: 'bg-warning', low: 'bg-destructive' }[confidenceColor(result.confidence)];

  return (
    <Card className="ix-edge-card p-(--space-card-lg)">
      <div className="grid gap-(--space-grid) sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="if-name">Field name</Label>
          <Input id="if-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. customer_email" className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="if-value">Sample value</Label>
          <Input id="if-value" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g. ada@acme.io" className="font-mono" />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => { setName(p.name); setValue(p.value); }}
            className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Pipeline stages — a dot flows through each in sequence. */}
      <div className="mt-(--space-card) flex items-center gap-2" aria-hidden="true">
        {STAGES.map((stage, i) => (
          <div key={stage} className="flex flex-1 items-center gap-2">
            <span className="ix-flow-dot inline-block size-2 shrink-0 rounded-full bg-primary" style={{ '--reveal-index': i } as CSSProperties} />
            <span className="truncate font-mono text-xs uppercase tracking-wider text-muted-foreground">{stage}</span>
            {i < STAGES.length - 1 && <span className="hidden h-px flex-1 bg-border sm:block" />}
          </div>
        ))}
      </div>

      {/* Result */}
      <div className="mt-(--space-card) rounded-(--card-radius) border border-border bg-section-secondary p-(--space-card)">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="font-display text-2xl font-bold text-foreground">{result.fieldType}</div>
            <div className="mt-1 font-mono text-xs uppercase tracking-wider text-muted-foreground">{result.classification}</div>
          </div>
          <Badge variant="secondary">{confidenceLabel(result.confidence)}</Badge>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
            <div className={cn('h-full rounded-full transition-[width] duration-slow ease-out', barClass)} style={{ width: `${pct}%` }} />
          </div>
          <span className="font-mono text-xs text-muted-foreground">{pct}%</span>
        </div>
      </div>
    </Card>
  );
}
